import { QueryDocumentSnapshot } from 'firebase/firestore'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useCollectionData } from 'react-firebase-hooks/firestore'
import { getDocs, Notification } from '../../firebase'
import { useAuthState } from '../../firebase/hooks'
import {
  sortedFutureNotificationsRef,
  sortedPastNotificationsRef,
} from '../../models/notification'
import { App } from '../../types'

const NOTIFICATIONS_LIMIT = 20

export const useNotifications = (app: App) => {
  const nowRef = useRef<Date>(new Date())

  const { user } = useAuthState()
  const [futureNotifications] = useCollectionData(
    user ? sortedFutureNotificationsRef(app, user.uid, nowRef.current) : null
  )

  const oldestNotification = useRef<QueryDocumentSnapshot<Notification> | null>(
    null
  )
  const [pastNotifications, setPastNotifications] = useState<Notification[]>([])
  const [fetching, setFetching] = useState(false)
  const [isLast, setIsLast] = useState(false)

  const loadMore = useCallback(async () => {
    if (!user) return
    if (isLast) return
    if (fetching) return
    setFetching(true)
    const loadedNotificationsSnapshot = await getDocs(
      sortedPastNotificationsRef(
        app,
        user.uid,
        nowRef.current,
        oldestNotification.current,
        NOTIFICATIONS_LIMIT
      )
    )
    const loadedNotificationDocs = loadedNotificationsSnapshot.docs
    const loadedNotifications = loadedNotificationDocs.map((doc) => doc.data())
    setPastNotifications([...pastNotifications, ...loadedNotifications])
    if (!loadedNotificationsSnapshot.empty) {
      oldestNotification.current = loadedNotificationDocs.slice(-1)[0]
    }
    if (loadedNotificationDocs.length < NOTIFICATIONS_LIMIT) {
      setIsLast(true)
    }
    setFetching(false)
  }, [pastNotifications, isLast, fetching, user])

  const notifications = useMemo(() => {
    return futureNotifications
      ? [...futureNotifications, ...pastNotifications]
      : pastNotifications
  }, [futureNotifications, pastNotifications])

  return { loadMore, notifications, fetching, isLast }
}
